import { useEffect } from "react";
import { useNavigate } from "react-router";
import {
  PRODUCT_KEYS,
  PRODUCT_LABELS,
  PRODUCT_LANDING_PATH,
  PRODUCT_WORDMARK,
  type ProductKey,
} from "../../../shared/products/keys";
import { useEnabledProducts } from "../../hooks/useEnabledProducts";
import { useActiveProductState } from "../../hooks/useActiveProduct";
import { writeLastProduct } from "./lastProduct";
import { wordmarkVariantFor } from "./productTheme";

/**
 * The products the switcher offers (spec §6): those the instance has AND the user holds a role in,
 * in PRODUCT_KEYS order, and only when there are two or more. A single-product instance, a legacy
 * one, or a user holding one product gets none, and it never offers a product the user cannot use.
 * With the original two products this is exactly "the instance has both and the user holds a role
 * in each". A UI hint only: the server gates every product route itself.
 */
export function useSwitcherProducts(): readonly ProductKey[] {
  const { ready, has, holdsRole } = useEnabledProducts();
  if (!ready) return NONE;
  const held = PRODUCT_KEYS.filter((key) => has(key) && holdsRole(key));
  return held.length >= 2 ? held : NONE;
}
const NONE: readonly ProductKey[] = [];

/** Whether the switcher shows: see useSwitcherProducts. */
export function useProductSwitcherVisible(): boolean {
  return useSwitcherProducts().length > 0;
}

/*
 * Colour tokens only (UI guidelines §1): surface-inverted is the header bar's #242424, foreground
 * the DS text colour (also #242424), foreground-soft its softer step (#4e4e4e).
 *
 * Contrast (WCAG 2.x, measured against what each segment sits on):
 * - header: drawn in the header's own ink (header-foreground) on the header bar (header). Both
 *   products draw the same dark bar (2026-10-08 shell spec, D1): white and #242424, exactly the
 *   classes they replace: the pill is white/5 over the bar (≈ #2f2f2f), inactive text white/80
 *   (≈ #d5d5d5) is ≈ 9.1:1 there, the pressed segment is #242424 on white, 15.52:1. The focus ring
 *   is the ink at 80% with a bar-coloured offset, so it reads on the pressed segment as well as the
 *   idle one.
 * - sheet (the light mobile menu): inactive foreground-soft (#4e4e4e) on surface-muted (#f5f5f5) is
 *   ≈ 7.6:1; the pressed segment is white on surface-inverted, 15.52:1; the ring is
 *   surface-inverted.
 *
 * Every fill is important (`!`): index.css paints a pressed button inside any role="group" white,
 * unlayered, so a plain utility loses to it and the sheet's white "Hire" vanished (#1328).
 */
const STYLES = {
  header: {
    group:
      "flex shrink-0 items-center rounded-md border border-header-foreground/20 p-0.5 bg-header-foreground/5",
    ring: "focus-visible:ring-header-foreground/80 focus-visible:ring-offset-header",
    pressed: "bg-header-foreground! text-header!",
    idle: "text-header-foreground/80! hover:text-header-foreground! hover:bg-header-foreground/10",
  },
  sheet: {
    group:
      "flex w-fit items-center rounded-md border border-surface-strong dark:border-border p-0.5 bg-surface-muted dark:bg-muted",
    ring: "focus-visible:ring-surface-inverted dark:focus-visible:ring-foreground focus-visible:ring-offset-surface dark:focus-visible:ring-offset-background",
    pressed: "bg-surface-inverted! dark:bg-foreground! text-white! dark:text-background!",
    idle: "text-foreground-soft! hover:bg-surface-strong! dark:hover:bg-white/10!",
  },
} as const;

interface ProductSwitcherProps {
  /** "header" on the desktop header bar; "sheet" in the light mobile side menu. */
  variant?: "header" | "sheet";
  /** Called after a switch, so the mobile menu can close. */
  onSwitch?: () => void;
}

/**
 * Hire / Performance (spec §6, D14). Rendered only when useProductSwitcherVisible. The pressed tab
 * is the active product (useActiveProduct), so on a shared page such as Users it is the product the
 * user is in. A click saves the choice per instance (localStorage, D8), then goes to that product's
 * landing with the CURRENT ?instanceid= kept (the catch-all redirect drops it).
 *
 * The instance comes from the URL, the same source HomeRoute reads the preference with, not from
 * useHeaderData: that hook fetches tenants and instances on every mount, and the header mounts this
 * twice (desktop bar and mobile menu).
 */
export function ProductSwitcher({ variant = "header", onSwitch }: ProductSwitcherProps) {
  const offered = useSwitcherProducts();
  const visible = offered.length > 0;
  const offeredKey = offered.join(",");
  const { product: active, instanceId } = useActiveProductState();
  const navigate = useNavigate();

  // Warm the other wordmark so the header repaints from cache on a switch. Only for a user who can
  // switch: a hire-only header requests nothing new.
  useEffect(() => {
    if (!offeredKey) return;
    for (const key of offeredKey.split(",") as ProductKey[])
      new Image().src = PRODUCT_WORDMARK[key][wordmarkVariantFor(key)];
  }, [offeredKey]);

  if (!visible) return null;

  const go = (key: ProductKey) => {
    if (key === active) return;
    // Saved first, then navigated (skyAdvisor's ModeToggle): the shell repaints in the chosen
    // product at once, and a shared page opened afterwards keeps it.
    if (instanceId) writeLastProduct(instanceId, key);
    const query = instanceId ? `?instanceid=${encodeURIComponent(instanceId)}` : "";
    navigate(`${PRODUCT_LANDING_PATH[key]}${query}`);
    onSwitch?.();
  };

  const styles = STYLES[variant];
  return (
    <div
      role="group"
      aria-label="Product"
      className={styles.group}
    >
      {offered.map((key) => {
        const pressed = key === active;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={pressed}
            onClick={() => go(key)}
            className={`px-3 h-7 text-xs font-semibold rounded-[5px] whitespace-nowrap transition-colors focus:outline-none focus-visible:relative focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-offset-2 ${styles.ring} ${pressed ? styles.pressed : styles.idle}`}
          >
            {PRODUCT_LABELS[key]}
          </button>
        );
      })}
    </div>
  );
}

export default ProductSwitcher;
